/**
 * Resolves the solvtax-api HTTP origin (scheme + host, no path suffix).
 *
 * Priority:
 *  1. REACT_APP_API_URL or REACT_APP_API_BASE_URL (build-time; strip trailing /api)
 *  2. Local → http://localhost:3001
 *  3. Deployed without env → same-origin (unified Netlify site)
 *
 * Split deploy: set REACT_APP_API_URL=https://<api-site>.netlify.app on the
 * frontend Netlify site (do not include a trailing /api: routes are /api/v1/...).
 */

const LOCAL_API_ORIGIN = "http://localhost:3001";

function isLocalHostname(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname.startsWith("192.168.");
}

/** @returns {boolean} True when the app is running against a local dev host. */
export function isLocalEnvironment() {
  if (typeof window !== "undefined" && window.location?.hostname) {
    return isLocalHostname(window.location.hostname);
  }
  return process.env.NODE_ENV === "development";
}

/**
 * Normalize a configured API URL to an origin (no path, no trailing slash).
 * Accepts either https://host or https://host/api (Clumse-style).
 */
export function normalizeApiOrigin(raw) {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim().replace(/\/$/, "");
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed.includes("://") ? trimmed : `https://${trimmed}`);
    // If path is exactly /api, treat as origin-only (SolvTax routes already include /api/v1)
    if (url.pathname === "/api" || url.pathname === "/") {
      return url.origin;
    }
    // Custom path prefix: keep origin only; path segments are owned by api.js
    return url.origin;
  } catch {
    return trimmed.replace(/\/api\/?$/, "");
  }
}

function getBuildTimeApiOrigin() {
  const envUrl =
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL);

  return normalizeApiOrigin(envUrl);
}

/**
 * Active solvtax-api origin.
 * @returns {string}
 */
export function getSolvtaxApiOrigin() {
  const fromEnv = getBuildTimeApiOrigin();
  if (fromEnv) {
    return fromEnv;
  }

  if (isLocalEnvironment()) {
    return LOCAL_API_ORIGIN;
  }

  // Unified Netlify site: SPA + Functions on the same origin
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  return LOCAL_API_ORIGIN;
}
